// ============================================================================
// DYNAMIC FIELD INPUT COMPONENT
// Renders form inputs based on field type definitions from templates
// Date: October 17, 2025
// Updated: November 7, 2025 - Added dynamic data loading for select fields
// ============================================================================

'use client';

import React, { useState, useEffect } from 'react';
import { authService } from '@/lib/authService';

// ============================================================================
// TYPES
// ============================================================================

export interface FieldDefinition {
  key: string;
  label: string;
  type: 'text' | 'number' | 'date' | 'datetime' | 'select' | 'multi-select' | 'boolean' | 'textarea';
  required?: boolean;
  placeholder?: string;
  help?: string;
  help_text?: string; // Alternative name from form_configurations
  options?: Array<{ value: string | number; label: string }>;
  min?: number;
  max?: number;
  prefix?: string;
  suffix?: string;
  validation?: {
    pattern?: string;
    message?: string;
  };
  data_source?: string; // e.g., 'users_with_module_access'
  module_key?: string; // For filtering users by module
}

interface DynamicFieldInputProps {
  field: FieldDefinition;
  value: any;
  onChange: (value: any) => void;
  disabled?: boolean;
  className?: string;
}

// ============================================================================
// COMPONENT
// ============================================================================

export default function DynamicFieldInput({ 
  field, 
  value, 
  onChange, 
  disabled = false,
  className = ''
}: DynamicFieldInputProps) {
  const [dynamicOptions, setDynamicOptions] = useState<Array<{ value: string | number; label: string }>>([]);
  const [loadingOptions, setLoadingOptions] = useState(false);

  // Load dynamic options if data_source is specified
  useEffect(() => {
    const loadDynamicOptions = async () => {
      if (!field.data_source) return;

      setLoadingOptions(true);
      try {
        const token = authService.getTokens()?.access_token;
        if (!token) return;

        let url = '';
        
        // Handle different data sources
        switch (field.data_source) {
          case 'users_with_module_access':
            // Load users with access to specific module
            const moduleKey = field.module_key || 'total-operations';
            url = `/api/v1/module-access/users?module_key=${moduleKey}`;
            break;
          
          case 'all_users':
            url = '/api/v1/users';
            break;
          
          case 'contacts':
            url = '/api/v1/contacts';
            break;
          
          default:
            console.warn(`Unknown data_source: ${field.data_source}`);
            return;
        }

        const response = await fetch(url, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          const data = await response.json();
          
          if (data.success && data.data) {
            // Format options based on data source
            if (field.data_source === 'users_with_module_access' || field.data_source === 'all_users') {
              const options = data.data.map((user: any) => {
                const name = [user.first_name, user.last_name].filter(Boolean).join(' ') || user.email;
                return {
                  value: user.id,
                  label: `${name} (${user.email})`
                };
              });
              setDynamicOptions(options);
            } else if (field.data_source === 'contacts') {
              const options = data.data.map((contact: any) => ({
                value: contact.id,
                label: contact.name || contact.email
              }));
              setDynamicOptions(options);
            }
          }
        }
      } catch (error) {
        console.error('Error loading dynamic options:', error);
      } finally {
        setLoadingOptions(false);
      }
    };

    loadDynamicOptions();
  }, [field.data_source, field.module_key]);

  // Use dynamic options if available, otherwise use static options
  const effectiveOptions = dynamicOptions.length > 0 ? dynamicOptions : (field.options || []);
  const helpText = field.help || field.help_text;
  
  const renderInput = () => {
    const baseInputClass = `w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed ${className}`;
    
    switch (field.type) {
      case 'text':
        return (
          <input
            type="text"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder}
            required={field.required}
            pattern={field.validation?.pattern}
            title={field.validation?.message}
            disabled={disabled}
            className={baseInputClass}
          />
        );
      
      case 'number':
        return (
          <div className="flex items-center">
            {field.prefix && (
              <span className="text-gray-600 font-medium mr-2">{field.prefix}</span>
            )}
            <input
              type="number"
              value={value ?? ''}
              onChange={(e) => {
                const val = e.target.value;
                onChange(val === '' ? null : parseFloat(val));
              }}
              placeholder={field.placeholder}
              required={field.required}
              min={field.min}
              max={field.max}
              step="any"
              disabled={disabled}
              className={`flex-1 ${baseInputClass}`}
            />
            {field.suffix && (
              <span className="text-gray-600 font-medium ml-2">{field.suffix}</span>
            )}
          </div>
        );
      
      case 'date':
        return (
          <input
            type="date"
            value={value ? (typeof value === 'string' ? value.split('T')[0] : value) : ''}
            onChange={(e) => onChange(e.target.value)}
            required={field.required}
            disabled={disabled}
            className={baseInputClass}
          />
        );
      
      case 'datetime':
        return (
          <input
            type="datetime-local"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            required={field.required}
            disabled={disabled}
            className={baseInputClass}
          />
        );
      
      case 'select':
        return (
          <select
            value={value || ''}
            onChange={(e) => {
              const selectedValue = e.target.value;
              // Convert to number if all options are numbers
              const isNumeric = effectiveOptions.every(opt => typeof opt.value === 'number');
              onChange(selectedValue === '' ? null : (isNumeric ? Number(selectedValue) : selectedValue));
            }}
            required={field.required}
            disabled={disabled || loadingOptions}
            className={baseInputClass}
          >
            <option value="">
              {loadingOptions ? 'Loading...' : (field.placeholder || 'Select...')}
            </option>
            {effectiveOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        );
      
      case 'multi-select':
        const currentValues = Array.isArray(value) ? value : [];
        
        return (
          <div className="space-y-2 max-h-48 overflow-y-auto border border-gray-300 rounded-lg p-2">
            {loadingOptions ? (
              <div className="text-center py-4 text-gray-500">Loading options...</div>
            ) : (
              effectiveOptions.map((opt) => (
                <label
                  key={opt.value}
                  className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-2 rounded"
                >
                  <input
                    type="checkbox"
                    checked={currentValues.includes(opt.value)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        onChange([...currentValues, opt.value]);
                      } else {
                        onChange(currentValues.filter((v: string | number) => v !== opt.value));
                      }
                    }}
                    disabled={disabled}
                    className="rounded text-blue-600 focus:ring-2 focus:ring-blue-500"
                  />
                  <span className="text-sm">{opt.label}</span>
                </label>
              ))
            )}
          </div>
        );
      
      case 'boolean':
        return (
          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="checkbox"
              checked={value || false}
              onChange={(e) => onChange(e.target.checked)}
              disabled={disabled}
              className="w-5 h-5 rounded text-blue-600 focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed"
            />
            <span className="text-sm font-medium text-gray-700">
              {field.label}
            </span>
          </label>
        );
      
      case 'textarea':
        return (
          <textarea
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder}
            required={field.required}
            disabled={disabled}
            rows={4}
            className={baseInputClass}
          />
        );
      
      default:
        return (
          <input
            type="text"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder}
            disabled={disabled}
            className={baseInputClass}
          />
        );
    }
  };
  
  // Boolean fields have their label inside, others outside
  if (field.type === 'boolean') {
    return (
      <div className="flex flex-col space-y-1">
        {renderInput()}
        {helpText && (
          <p className="text-xs text-gray-500 ml-8">{helpText}</p>
        )}
      </div>
    );
  }
  
  return (
    <div className="flex flex-col space-y-1">
      <label className="text-sm font-medium text-gray-700">
        {field.label}
        {field.required && <span className="text-red-500 ml-1">*</span>}
      </label>
      {renderInput()}
      {helpText && (
        <p className="text-xs text-gray-500">{helpText}</p>
      )}
      {!helpText && field.validation?.message && (
        <p className="text-xs text-gray-500">{field.validation.message}</p>
      )}
    </div>
  );
}
